/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  sendOnboardingOTP,
  verifyOnboardingOTP,
  setRegistrationEmail,
  resetRegistrationFlow,
} from '../redux/slice/registerSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomNavbar from '../components/CustomNavbar';
import FooterTwo from '../components/Footer/FooterTwo';
import FooterData from '../components/Footer/FooterData';
import { Stepper, Step, StepLabel } from '@mui/material';

function Copyright() {
  return (
    <div style={{ 
      textAlign: 'center', 
      fontSize: '14px', 
      color: '#666',
      marginTop: '32px'
    }}>
      Copyright © 2024. Abhay Education Pvt. Ltd. All rights reserved.
    </div>
  ); 
}

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((store) => store.register);
  const [email, setEmail] = useState(auth.submittedEmail || '');
  const [otpVal, setOtpVal] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    try {
      // Step 1: Send OTP first (correct flow)
      const response = await dispatch(sendOnboardingOTP(email)).unwrap();
      
      if (response.success) {
        dispatch(setRegistrationEmail(email));
        toast.success(response.message || 'OTP sent to your email successfully!');
      }
    } catch (error) {
      console.error('Send OTP Error:', error);
      
      // Handle specific error responses
      if (error?.action === 'redirect_to_expert_login') {
        toast.error('You are already registered and approved as an expert. Please login through the expert portal.');
        setTimeout(() => navigate('/tutor_dashboard/login'), 2000);
      } else if (error?.action === 'register') {
        toast.error('Expert not found or email not verified. Please register first.');
      } else if (error?.action === 'application_rejected') {
        toast.error('Your application has been rejected. Please contact support.');
      } else if (error?.message?.includes('not found')) {
        toast.error('Expert not found or email not verified. Please ensure you have registered with this email.');
      } else {
        toast.error(error?.message || 'Failed to send OTP. Please try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpVal || otpVal.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      // Step 2: Login with the OTP received in email (correct flow)
      const result = await dispatch(verifyOnboardingOTP({ email: email, otp_entered: otpVal })).unwrap();
      
      if (result.success && result.token) {
        toast.success(result.message || 'Registration successful!');
        dispatch(setRegistrationEmail(''));
        
        // Store token and navigate to dashboard
        localStorage.setItem('registerToken', result.token);
        
        // Small delay to show success message
        setTimeout(() => {
          navigate('/register-dashboard', { replace: true });
        }, 1000);
      } else {
        toast.error('Registration failed. Invalid response from server.');
      }
    } catch (error) {
      toast.error(error?.message || 'Registration failed. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    if (!email) {
      toast.error('Email is missing. Please start over.');
      return;
    }

    try {
      const response = await dispatch(sendOnboardingOTP(email)).unwrap();
      
      if (response.success) {
        toast.success(response.message || 'OTP resent successfully!');
        setOtpVal(''); // Clear previous OTP input
      }
    } catch (error) {
      console.error('Resend OTP Error:', error);
      toast.error(error?.message || 'Failed to resend OTP. Please try again.');
    }
  };

  return (
    <div className="body_wrapper">
      <CustomNavbar nClass='m-auto' hbtnClass='new_btn' tutorSignup={true} />

      <section className="sign_in_area bg_color sec_pad" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(120deg, #e0e7ff 0%, #f7e6ff 100%)',
        fontFamily: "'Inter', sans-serif",
        padding: '40px 0',
      }}>
        <div className="container" style={{ maxWidth: 980 }}>
          <div className="sign_info" style={{
            boxShadow: '0 8px 32px rgba(60,60,60,0.10)',
            borderRadius: 18,
            background: '#fff',
            padding: 0,
            overflow: 'hidden',
            display: 'flex',
            flexWrap: 'wrap',
          }}>
            {/* Left Side Image */}
            <div className="col-lg-6 d-none d-lg-flex" style={{
              background: 'linear-gradient(120deg, #4caf50 0%, #81c784 100%)',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 520,
              padding: 0,
            }}>
              <div style={{
                width: '80%',
                maxWidth: 340,
                borderRadius: 16,
                margin: 'auto',
                boxShadow: '0 4px 24px rgba(76, 175, 80, 0.10)',
                background: '#fff',
                padding: 32,
                textAlign: 'center',
              }}>
                <div style={{
                  width: 80,
                  height: 80,
                  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                    stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <h3 style={{ color: '#4caf50', marginBottom: 16, fontSize: 24, fontWeight: 700 }}>
                  Join Our Platform
                </h3>
                <p style={{ color: '#666', fontSize: 16, lineHeight: 1.6 }}>
                  Create your account to unlock exclusive features and opportunities.
                </p>
              </div>
            </div>

            {/* Right Side Form */}
            <div className="col-lg-6 col-12" style={{
              flex: 1,
              minWidth: 320,
              padding: '48px 32px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}>
              <div className="login_info" style={{ width: '100%', maxWidth: 400, margin: '0 auto' }}>
                <div style={{
                  width: 60,
                  height: 60,
                  background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                    stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>

                <h2 style={{
                  marginBottom: 32,
                  textAlign: 'center',
                  fontWeight: 700,
                  color: '#4caf50',
                  fontSize: 28,
                }}>
                  Create Account
                </h2>

                {/* Material UI Stepper */}
                <Stepper activeStep={auth.isOtpSent ? 1 : 0} alternativeLabel sx={{ mb: 3 }}>
                  <Step>
                    <StepLabel>Enter Email</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Verify OTP</StepLabel>
                  </Step>
                </Stepper>

                <form onSubmit={auth.isOtpSent ? handleSubmit : handleSendOtp}>
                  {/* Email Input */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{
                      display: 'block',
                      marginBottom: 8,
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#374151'
                    }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={auth.isOtpSent}
                      required
                      autoFocus={!auth.isOtpSent}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1.5px solid #e0e6ed',
                        borderRadius: 8,
                        fontSize: 16,
                        outline: 'none',
                        background: auth.isOtpSent ? '#f5f5f5' : '#f9fbfd',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#4caf50'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e6ed'}
                    />
                  </div>

                  {/* OTP Input */}
                  {auth.isOtpSent && (
                    <div style={{ marginBottom: 20 }}>
                      <label style={{
                        display: 'block',
                        marginBottom: 8,
                        fontSize: 14,
                        fontWeight: 500,
                        color: '#374151'
                      }}>
                        Enter OTP
                      </label>
                      <input
                        type="text"
                        placeholder="6-digit OTP"
                        value={otpVal}
                        onChange={(e) => setOtpVal(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        autoFocus
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1.5px solid #e0e6ed',
                          borderRadius: 8,
                          fontSize: 16,
                          outline: 'none',
                          background: '#f9fbfd',
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#4caf50'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e6ed'}
                      />
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={auth.isLoading}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      background: auth.isLoading ? '#ccc' : 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: auth.isLoading ? 'not-allowed' : 'pointer',
                      marginBottom: 16,
                      boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                      transition: 'all 0.2s',
                    }}
                  >
                    {auth.isLoading
                      ? 'Processing...'
                      : auth.isOtpSent
                      ? 'Verify OTP'
                      : 'Send OTP'}
                  </button>

                  {/* Resend OTP */}
                  {auth.isOtpSent && (
                    <div style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={auth.isLoading}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#4caf50',
                          fontSize: 14,
                          cursor: auth.isLoading ? 'not-allowed' : 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Didn't receive OTP? Resend
                      </button>
                    </div>
                  )}
                </form>

                {/* Login Link */}
                <div style={{
                  textAlign: 'center',
                  marginTop: 24,
                  fontSize: '14px',
                  color: '#666'
                }}>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/tutor_dashboard/login')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#4caf50',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Sign In
                  </button>
                </div>

                <Copyright />
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTwo FooterData={FooterData} />
      <ToastContainer position='top-right' autoClose={5000} hideProgressBar newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </div>
  );
};

export default Register;
